import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Divider,
  useTheme,
  CircularProgress,
  Button,
  Stack,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import { useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PassagemPipelineStage from "../../components/Passagens/PassagemPipelineStage";
import PassagemDetailDrawer from "../Passagens/PassagemDetailDrawer";
import { salePipelineStages } from "../../data/ticketSalesPipeline/constants";
import { getPassagensPipeline } from "../../api/passagensApi";
import { getAgents, triggerPassagemRecovery } from "../../api/client";

const RECOVERY_STAGE_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const PIPELINE_MAX_RANGE_DAYS = 92;

function defaultMonthRange() {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { start: fmt(start), end: fmt(end) };
}

function clampPipelineDateRange(start, end, maxDays = PIPELINE_MAX_RANGE_DAYS) {
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime())) {
    return { start, end, clamped: false };
  }
  const diffDays = Math.round((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays <= maxDays) return { start, end, clamped: false };
  const clampedStart = new Date(endDate);
  clampedStart.setDate(clampedStart.getDate() - maxDays);
  return {
    start: clampedStart.toISOString().slice(0, 10),
    end,
    clamped: true,
    requestedStart: start,
  };
}

const TicketSalesPipeline = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailId, setDetailId] = useState(null);
  const [recoverySnack, setRecoverySnack] = useState({ open: false, message: "", severity: "info" });
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const defaults = useMemo(() => defaultMonthRange(), []);

  const filters = useMemo(() => {
    const rawStart = searchParams.get("start") || defaults.start;
    const rawEnd = searchParams.get("end") || defaults.end;
    return clampPipelineDateRange(rawStart, rawEnd);
  }, [searchParams, defaults]);

  const { data, isLoading, isFetching, isPreviousData } = useQuery(
    ["passagens-pipeline", filters.start, filters.end],
    () =>
      getPassagensPipeline({
        start: filters.start,
        end: filters.end,
        limit: 500,
        enrich_mp: 0,
      }),
    { staleTime: 30_000, keepPreviousData: true }
  );

  const showInitialLoading = isLoading && !data && !isPreviousData;

  const { data: agentsPayload } = useQuery(
    ["agents-recovery"],
    () => getAgents({ page: 1, limit: 10, skill_key: "passagem_recovery" }),
    { staleTime: 60_000 }
  );

  const recoveryAgentId = useMemo(() => {
    const list = agentsPayload?.data || [];
    const rows = Array.isArray(list) ? list : [];
    return rows[0]?.id || null;
  }, [agentsPayload]);

  const handleRecover = useCallback(
    async (card) => {
      if (!card?.leadId) return;
      if (!recoveryAgentId) {
        setRecoverySnack({
          open: true,
          severity: "warning",
          message: t("passagensPage.recoveryNoAgent", {
            defaultValue: "Configure um agente ativo com a função Recuperação de vendas (passagens).",
          }),
        });
        return;
      }
      setRecoveryLoading(true);
      try {
        const res = await triggerPassagemRecovery(recoveryAgentId, card.leadId);
        if (res?.statusCode >= 400 || res?.message?.includes?.("não elegível")) {
          throw new Error(res?.message || "Lead não elegível");
        }
        setRecoverySnack({
          open: true,
          severity: "success",
          message: t("passagensPage.recoveryTriggered", { defaultValue: "Recuperação iniciada para este cliente." }),
        });
      } catch (err) {
        setRecoverySnack({
          open: true,
          severity: "error",
          message: err?.message || t("passagensPage.recoveryError", { defaultValue: "Não foi possível iniciar a recuperação." }),
        });
      } finally {
        setRecoveryLoading(false);
      }
    },
    [recoveryAgentId, t]
  );

  const pipeline = useMemo(() => {
    const raw = data?.pipeline || {};
    const next = {};
    salePipelineStages.forEach((s) => {
      next[s.id] = Array.isArray(raw[s.id]) ? raw[s.id] : Array.isArray(raw[String(s.id)]) ? raw[String(s.id)] : [];
    });
    return next;
  }, [data?.pipeline]);

  const totalCards = useMemo(
    () => Object.values(pipeline).reduce((sum, cards) => sum + (cards?.length || 0), 0),
    [pipeline]
  );

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const handleOpenLead = (leadId) => {
    if (!leadId) return;
    const width = window.screen.width;
    const height = window.screen.height;
    window.open(
      `/popup-call/${leadId}`,
      "SolicitacaoPopup",
      `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: theme.palette.background.default, minHeight: "100vh" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h4" color={theme.palette.text.primary}>
          {t("passagensPage.pipelineTitle", { defaultValue: "Pipeline de Venda de Passagens" })}
        </Typography>
        <Button size="small" startIcon={<ListAltIcon />} onClick={() => navigate("/passagens")}>
          {t("passagensPage.listView", { defaultValue: "Lista" })}
        </Button>
      </Stack>
      <Typography variant="body1" color={theme.palette.text.secondary} gutterBottom>
        {t("passagensPage.pipelineSubtitle", {
          defaultValue: "Visão Kanban das vendas em andamento e concluídas (somente leitura).",
        })}
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2, mt: 1, flexWrap: "wrap" }}>
        <TextField
          size="small"
          type="date"
          label={t("passagensPage.filterFrom", { defaultValue: "De" })}
          value={filters.start}
          onChange={(e) => setFilter("start", e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label={t("passagensPage.filterTo", { defaultValue: "Até" })}
          value={filters.end}
          onChange={(e) => setFilter("end", e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        {isFetching && !showInitialLoading ? <CircularProgress size={22} sx={{ alignSelf: "center" }} /> : null}
      </Stack>

      {filters.clamped && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("passagensPage.pipelineDateClamped", {
            defaultValue:
              "Período limitado a {{days}} dias para carregar mais rápido. Ajuste as datas para um intervalo menor.",
            days: PIPELINE_MAX_RANGE_DAYS,
          })}
        </Alert>
      )}

      <Divider sx={{ mb: 3 }} />

      {showInitialLoading ? (
        <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 2 }}>
          {salePipelineStages.map((stage) => (
            <Box
              key={stage.id}
              sx={{
                minWidth: 300,
                maxWidth: 340,
                p: 1.25,
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {stage.name}
              </Typography>
              <CircularProgress size={20} />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 2, overflowX: "auto", scrollBehavior: "smooth", pb: 2 }}>
          {salePipelineStages.map((stage) => (
            <PassagemPipelineStage
              key={stage.id}
              stage={stage}
              cards={pipeline[stage.id] || []}
              totalCards={totalCards}
              onOpenVenda={setDetailId}
              onOpenLead={handleOpenLead}
              onRecover={recoveryLoading ? undefined : handleRecover}
              recoverableStageIds={RECOVERY_STAGE_IDS}
            />
          ))}
        </Box>
      )}

      <PassagemDetailDrawer open={detailId != null} passagemId={detailId} onClose={() => setDetailId(null)} />

      <Snackbar
        open={recoverySnack.open}
        autoHideDuration={6000}
        onClose={() => setRecoverySnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={recoverySnack.severity} onClose={() => setRecoverySnack((s) => ({ ...s, open: false }))}>
          {recoverySnack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TicketSalesPipeline;
